﻿import {computed, defineComponent, onUnmounted, type PropType, ref, toRefs, watch} from 'vue';
import {useFieldArray, useForm} from 'vee-validate';
import * as yup from 'yup';
import {availableCountries} from "@/app/shared/helpers/country.ts";
import {useLocationStore} from "@/app/stores/location-store.ts";
import { checkVAT, countries } from 'jsvat';
import {AthumiService} from "@/api/enterprise-gateway-api.proxy.ts";

export interface BillingAddress {
    street: string;
    houseNumber: string;
    boxNumber?: string;
    postalCode: string;
    city: string;
    country: string;
    emails: { service: string, email: string }[]
    showEmail: boolean;
    showVatNumber: boolean;
    vatNumber?: string;
    identifier?: string;
    isCitizen: boolean;
    fullName?: string;
}

export const errorMessage = 'Het opgegeven btw-nummer is ongeldig. Contacteer accounting@athumi.eu indien u toch facturen op dit btw-nummer wenst.';

export const addressValidationSchema = yup.object({
    street: yup.string().required('Straat is verplicht'),
    houseNumber: yup.number().typeError('Voer een geldig huisnummer in').required('Huisnummer is verplicht'),
    boxNumber: yup.string().nullable(),
    postalCode: yup.string().required('Postcode is verplicht'),
    city: yup.string().required('Gemeente is verplicht'),
    country: yup.string().default('België').required('Land is verplicht'),
    emails: yup.array().of(
        yup.object({
            service: yup.string().required(),
            email: yup.string().email('Ongeldig e-mailadres').matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Ongeldig e-mailadres').when('$showEmail', {
                is: true,
                then: (schema) => schema.required('E-mailadres is verplicht'),
                otherwise: (schema) => schema.nullable()
            }),
        })
    ).nullable(),
    vatNumber: yup.string().when('showVatNumber', {
        is: true,
        then: (schema) => schema
            .required('BTW-nummer is verplicht')
            .test('vat-number', errorMessage, (value) => {
                const vatCheckResult = checkVAT(value, countries); 
                return vatCheckResult.isValid;
            }),
        otherwise: (schema) => schema.nullable()
    }),
    showEmail: yup.boolean(),
    showVatNumber: yup.boolean()
});

const streetMinLength: number = 2;

export default defineComponent({
    name: 'AegAddressForm',
    props: {
        address: {
            type: Object as PropType<BillingAddress>,
            required: true,
        },
        readOnly: {
            type: Boolean,
            required: false,
            default: true,
        },
        annotation: {
            type: String,
            required: false
        },
        service: {
            type: String as PropType<AthumiService>,
            required: false,
            default: AthumiService.ELys
        }
    },
    emits: ['update:address', 'validated'],
    setup(props, {emit}) {
        const locationStore = useLocationStore();
        
        const hasAddress = computed((): boolean => {
            return Boolean(props.address.street && props.address.houseNumber && props.address.postalCode && props.address.city && props.address.country);
        });

        const showVatNumber = ref<boolean>(props.address.showVatNumber || false);
        const showEmail = ref<boolean>(props.address.showEmail);
        
        const {defineField, meta, values, setValues, errors, validate} = useForm<BillingAddress>({
            validationSchema: addressValidationSchema,
            initialValues: {...props.address},
            validateOnMount: hasAddress.value ||showVatNumber.value,
        });

        const { fields: emails } = useFieldArray<{service: string, email: string}>('emails');

        const [street, streetProps] = defineField('street');
        const [houseNumber, houseNumberProps] = defineField('houseNumber');
        const [boxNumber, boxNumberProps] = defineField('boxNumber');
        const [postalCode, postalCodeProps] = defineField('postalCode');
        const [city, cityProps] = defineField('city');
        const [country, countryProps] = defineField('country');
        const [vatNumber, vatNumberProps] = defineField('vatNumber');

        const autocompleteActive = computed(() => {
            return country.value && country.value.toLowerCase() === 'belgië';
        });

        const readOnly = ref<boolean>(props.readOnly);
        const isCitizen = ref<boolean>(props.address.isCitizen);
        const fullName = ref<string>(props.address.fullName || '');
        
        const toggleReadOnly = () => {
            readOnly.value = false;
        };

        const {streetData, streetDataFetching, houseNumberData, houseNumberDataFetching} = toRefs(locationStore);

        const onStreetInputChange = async (newValue: string) => {
            street.value = newValue;
            if (autocompleteActive.value && newValue?.length > streetMinLength) {
                await locationStore.fetchStreetSuggestions(newValue);
            }
        }

        const onHouseNumberInputChange = async (newValue: string) => {
            houseNumber.value = newValue;
            if (autocompleteActive.value && street.value && city.value) {
                await locationStore.fetchHouseNumberSuggestions(street.value, city.value, newValue);
            }
        }

        const onStreetSelect = async (streetSuggestion: string) => {
            const newAddress = locationStore.parseBillingAddress(streetSuggestion);
            if (!newAddress.street) {
                return;
            }
            street.value = newAddress.street;
            if (newAddress.houseNumber) {
                houseNumber.value = newAddress.houseNumber;
            } else {
                if (!houseNumber.value) {
                    focusStreetNumber();
                }
            }
            if (newAddress.boxNumber) {
                boxNumber.value = newAddress.boxNumber;
            }
            if (newAddress.postalCode) {
                postalCode.value = newAddress.postalCode;
            } else {
                if (newAddress.city && newAddress.street) {
                    postalCode.value = await locationStore.fetchPostalCodes(newAddress.street, newAddress.city) || '';
                }
            }
            if (newAddress.city) {
                city.value = newAddress.city;
            }
        }

        const onHouseNumberSelect = (houseNumberSuggestion: string) => {
            const newHouseNumberAndBox = locationStore.parseHouseNumberAndBoxNumber(houseNumberSuggestion);
            houseNumber.value = newHouseNumberAndBox.houseNumber;
            boxNumber.value = newHouseNumberAndBox.boxNumber;
        }

        const focusStreetNumber = () => {
            const element = document.querySelector('input[name="nummer"]') as HTMLInputElement | null;
            element?.focus();
        };

        const updateModelValue = (newVals: BillingAddress) => {
            emit('update:address', {...newVals});
        };

        const annotation = computed((): string => {
            const service = emails.value[0];
            return !!props.annotation ? props.annotation : `Vul hier een e-mailadres in waarop uw organisatie vragen omtrent facturatie wenst te ontvangen voor ${service.value.service}. U ontvangt de factuur via Peppol indien u daarop bent aangesloten.`;
        }); 

        watch(() => props.address, (newValue) => {
            // Only update if the values actually changed to avoid triggering Watcher 2 unnecessarily
            if (JSON.stringify(newValue) !== JSON.stringify(values)) {
                setValues({
                    ...newValue,
                }, false);
            }
            showVatNumber.value = newValue.showVatNumber;
            showEmail.value = newValue.showEmail;
            isCitizen.value = newValue.isCitizen;
            fullName.value = newValue.fullName || '';
            country.value = newValue.country || 'België';
        }, {deep: true});
        watch(() => props.readOnly, (newValue) => {
            readOnly.value = newValue;
            if (!newValue) {
                void validate();
            }
        });
        watch(() => ({...values}), updateModelValue, {deep: true});
        watch(() => meta.value.valid, () => {
            emit('validated', meta.value.valid)
        }, {deep: true});

        onUnmounted(() => {
            locationStore.reset();
        });

        return {
            countryOptions: availableCountries,
            street,
            streetProps,
            streetData,
            streetDataFetching,
            houseNumber,
            houseNumberProps,
            houseNumberData,
            houseNumberDataFetching,
            boxNumber,
            boxNumberProps,
            postalCode,
            postalCodeProps,
            city,
            cityProps,
            country,
            countryProps,
            vatNumber,
            vatNumberProps,
            errors,
            readOnly,
            annotation,
            isCitizen,
            fullName,
            showVatNumber,
            showEmail,
            emails,
            toggleReadOnly,
            onStreetInputChange,
            onStreetSelect,
            onHouseNumberInputChange,
            onHouseNumberSelect,
        }
    }
})
